/**
 * Servicio para procesar guías individualmente usando el endpoint consultar-y-actualizar-corporativo de Envía Colvanes
 */
import axiosInstance from '../../api/axiosInstance'
import type {
  ColvanesTrackUpdateResponse,
  ColvanesProcessProgress,
} from '../../types/cron/colvanesManual'

export class ColvanesTrackUpdateService {
  /**
   * Procesar guías individualmente
   * El endpoint procesa una guía a la vez
   */
  static async processGuides(
    guides: string[],
    onProgress?: (progress: ColvanesProcessProgress) => void
  ): Promise<{
    success: boolean
    message: string
    timestamp: string
    guides_queried: number
    guides_processed: number
    updated_count: number
    skipped_count: number
    not_found_count: number
    not_delivered_count: number
    delivered_count: number
    error_count: number
    details: Array<{
      guia: string
      estado_guia: string | null
      action: string
      reason: string
      order_updated?: {
        success: boolean
        order_id: string
        previous_status: string
        new_status: string
        error: string | null
      }
    }>
  }> {
    if (guides.length === 0) {
      throw new Error('No hay guías para procesar')
    }

    // Filtrar guías vacías o inválidas
    const validGuides = guides
      .map((g) => g.trim())
      .filter((g) => g.length > 0)

    if (validGuides.length === 0) {
      throw new Error('No hay guías válidas para procesar')
    }

    const totalGuides = validGuides.length
    let processedGuides = 0
    const allDetails: Array<{
      guia: string
      estado_guia: string | null
      action: string
      reason: string
      order_updated?: {
        success: boolean
        order_id: string
        previous_status: string
        new_status: string
        error: string | null
      }
    }> = []
    let totalUpdated = 0
    let totalSkipped = 0
    let totalNotFound = 0
    let totalNotDelivered = 0
    let totalDelivered = 0
    let totalErrors = 0

    // Procesar cada guía individualmente
    for (let i = 0; i < validGuides.length; i++) {
      const guia = validGuides[i]

      try {
        // Consultar endpoint corporativo
        const response = await axiosInstance.post<ColvanesTrackUpdateResponse>(
          '/guias/consultar-y-actualizar-corporativo',
          {
            guia: guia,
          }
        )

        const result = response.data

        // Determinar acción basada en el resultado
        let action = 'skipped'
        let reason = 'Estado no aplica para actualización'

        if (result.order_updated?.success) {
          // Verificar si realmente cambió el estado (no es útil actualizar al mismo estado)
          const previousStatus = result.order_updated.previous_status
          const newStatus = result.order_updated.new_status
          
          if (previousStatus === newStatus) {
            // No hay cambio real, se mantiene en el mismo estado
            action = 'skipped'
            reason = `Pedido ya está en estado "${newStatus}", no hay cambio necesario`
            totalSkipped++
          } else {
            // Hay cambio real de estado
            action = 'updated'
            reason = `Pedido actualizado de ${previousStatus} a ${newStatus}`
            totalUpdated++
            
            // Contar entregas
            if (result.estado_guia === 'ENTREGADA DIGITALIZADA') {
              totalDelivered++
            }
          }
        } else if (result.order_updated?.error) {
          action = 'error'
          reason = result.order_updated.error
          totalErrors++
        } else {
          // Estado no aplica para actualización
          if (result.estado_guia === 'DESPACHADA') {
            totalNotDelivered++
          } else {
            totalSkipped++
          }
        }

        // Agregar al detalle
        allDetails.push({
          guia: result.guia,
          estado_guia: result.estado_guia,
          action,
          reason,
          order_updated: result.order_updated,
        })

        processedGuides++

        // Notificar progreso
        if (onProgress) {
          onProgress({
            currentGuide: i + 1,
            totalGuides,
            processedGuides,
            currentGuideResult: result,
          })
        }

        // Pequeño delay entre guías para evitar sobrecarga
        if (i < validGuides.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500))
        }
      } catch (error: any) {
        console.error(`Error procesando guía ${guia}:`, error)

        // Agregar error al detalle
        allDetails.push({
          guia: guia,
          estado_guia: null,
          action: 'error',
          reason:
            error.response?.data?.message ||
            `Error al procesar: ${error.message}`,
        })

        processedGuides++
        totalErrors++

        // Notificar progreso incluso con error
        if (onProgress) {
          onProgress({
            currentGuide: i + 1,
            totalGuides,
            processedGuides,
          })
        }
      }
    }

    // Construir respuesta consolidada
    return {
      success: true,
      message: `Procesadas ${processedGuides} guías de ${totalGuides} total`,
      timestamp: new Date().toISOString(),
      guides_queried: validGuides.length,
      guides_processed: processedGuides,
      updated_count: totalUpdated,
      skipped_count: totalSkipped,
      not_found_count: totalNotFound,
      not_delivered_count: totalNotDelivered,
      delivered_count: totalDelivered,
      error_count: totalErrors,
      details: allDetails,
    }
  }
}

